import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useT } from '../i18n';
import { ErrorState } from './Feedback';
import { ButtonLink } from './Button';

/**
 * DC-01 / AR-13: a render error in one page must never blank the whole app. The boundary shows the regular
 * ErrorState card (what happened, what to do, error code) and resets when `resetKey` changes (navigation).
 *
 * After a deploy, an open tab may ask for a JS chunk that no longer exists ("Failed to fetch dynamically imported
 * module" / ChunkLoadError). That is not a bug the user can act on, so we reload the page once to pick up the new
 * build; a second failure within a minute shows the error card instead of looping.
 */

const RELOAD_KEY = 'btw.chunkReloadAt';
const RELOAD_GUARD_MS = 60_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string };
  if (e.name === 'ChunkLoadError') return true;
  const msg = String(e.message ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (CSS )?chunk [\w-]+ failed|Unable to preload CSS/i.test(msg);
}

/** Reload once per minute at most; returns true when a reload was started. */
export function reloadOnceForChunkError(storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeSession(), reload: () => void = () => window.location.reload(), nowMs = Date.now()): boolean {
  try {
    const last = Number(storage?.getItem(RELOAD_KEY) ?? 0);
    if (last && nowMs - last < RELOAD_GUARD_MS) return false;
    storage?.setItem(RELOAD_KEY, String(nowMs));
  } catch {
    /* storage blocked: still reload, the guard then relies on the user */
  }
  reload();
  return true;
}

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

interface Props {
  children: ReactNode;
  /** Changing this value (e.g. the pathname) clears a shown error. */
  resetKey?: string;
  /** Where "back to safety" points; defaults to the overview. */
  homeHref?: string;
  /** Test seam. */
  onChunkError?: (error: unknown) => boolean;
}
interface State {
  error: unknown;
  resetKey?: string;
  reloading: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: this.props.resetKey, reloading: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) return { resetKey: props.resetKey, error: null, reloading: false };
    return null;
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      const started = (this.props.onChunkError ?? (() => reloadOnceForChunkError()))(error);
      if (started) this.setState({ reloading: true });
      return;
    }
    // No PII here: only the error and React's component stack go to the console (and from there to monitoring).
    console.error('[ui] render error', error, info.componentStack);
  }

  private retry = () => this.setState({ error: null, reloading: false });

  render() {
    if (this.state.error === null) return this.props.children;
    if (this.state.reloading) return null;
    return <ErrorFallback error={this.state.error} onRetry={this.retry} homeHref={this.props.homeHref ?? '/'} />;
  }
}

function ErrorFallback({ error, onRetry, homeHref }: { error: unknown; onRetry: () => void; homeHref: string }) {
  const t = useT();
  return (
    <div className="error-boundary" role="alert" data-testid="error-boundary">
      <ErrorState error={error} onRetry={onRetry} savedNote={false} />
      <div>
        <ButtonLink to={homeHref} variant="secondary" size="sm" onClick={onRetry}>
          {t('notFound.action')}
        </ButtonLink>
      </div>
    </div>
  );
}
